import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Form, FormInstance, Input, InputNumber, Select, Switch } from "antd";
import { Dispatch, useEffect, useMemo } from "react";
import { useAppSelector } from "../../../../../app/store";
import CaretDownIcon from "../../../../../assets/icons/CaretDownIcon";
import DeleteIcon from "../../../../../assets/icons/DeleteIcon";
import DragAndDropIcon from "../../../../../assets/icons/DragAndDropIcon";
import TimeIcon from "../../../../../assets/icons/TimeIcon";
import {
  useGetAllDepartmentQuery,
  useGetAllEmployeesQuery,
} from "../../../../../features/superAdmin/employeeService/employeeManagementService";
import useError from "../../../../../hooks/useError";
import {
  TemplateRoles,
  TemplateType,
} from "../../../../../types/template.types";
import { TEMPLATE_TYPES } from "../../constants/ticket.constants";

const TemplateRole = ({
  field,
  remove,
  form,
  selectedRoles,
  setSelectedRoles,
  totalFields,
}: {
  field: any;
  remove: (index: number) => void;
  form: FormInstance<any>;
  selectedRoles: TemplateRoles[];
  setSelectedRoles: Dispatch<React.SetStateAction<TemplateRoles[]>>;
  totalFields: number;
}) => {
  // Constants for drag and drop
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: field.key });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const template = useAppSelector(
    (state) => state?.template.template as TemplateType,
  );
  const templateType = template?.type;

  // Watched values
  const deptId = Form.useWatch(["ticket_roles", field.name, "dept_id"], form);
  const employeeId = Form.useWatch(
    ["ticket_roles", field.name, "employee_id"],
    form,
  );

  // Queries
  const {
    data: roles,
    isError: isRolesError,
    error: rolesError,
    isLoading: isRolesLoading,
  } = useGetAllDepartmentQuery({
    params: {
      page: 1,
      limit: 100000000,
      status: "active",
    },
  });

  const {
    data: employees,
    isError: isEmployeesError,
    error: employeesError,
    isLoading: isEmployeesLoading,
  } = useGetAllEmployeesQuery(
    {
      params: {
        department: deptId as number,
        status: "active",
        page: 1,
        limit: 100000000,
      },
    },
    {
      skip: !deptId,
    },
  );

  // Generate Options
  const roleOptions = roles?.data?.map((role) => ({
    label: role?.name,
    value: role?.id,
  }));

  const employeeOptions = useMemo(() => {
    if (!employees?.data?.employees) return [];

    // Get all selected employee IDs, excluding undefined
    const selectedEmployeeIds =
      selectedRoles
        ?.map((r) => r.employee_id)
        .filter((id): id is number => typeof id === "number") || [];

    return employees.data.employees
      .map((employee) => ({
        label: employee.officialInfo?.name || "Unnamed",
        value: employee.id,
      }))
      .filter(
        (option) =>
          !selectedEmployeeIds.includes(option.value) ||
          option.value === employeeId,
      );
  }, [selectedRoles, employees, employeeId]);

  // Effects
  useEffect(() => {
    const templateRoles = form.getFieldValue("ticket_roles");
    setSelectedRoles(templateRoles);
  }, [employeeId, form, setSelectedRoles]);

  // Errors
  useError(isRolesError, rolesError, "Departments get failed.");
  useError(isEmployeesError, employeesError, "Employees get failed.");

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="flex justify-between items-center mb-4 border border-slate-200 rounded-md p-3 select-none bg-white"
    >
      <div className="flex items-start justify-between w-full gap-2">
        <div className="cursor-pointer mt-6" {...listeners}>
          <DragAndDropIcon />
        </div>

        <Form.Item
          name={[field.name, "dept_name"]}
          rules={[{ required: true, message: "Please select a department" }]}
          className="w-48 mb-0 !hidden"
        >
          <Input size="large" hidden />
        </Form.Item>

        <Form.Item
          name={[field.name, "dept_id"]}
          rules={[{ required: true, message: "Please select a department" }]}
          className="w-48 mb-0"
        >
          <Select
            size="large"
            allowClear
            placeholder="Department"
            suffixIcon={<CaretDownIcon />}
            options={roleOptions || []}
            disabled={isRolesLoading}
            onChange={(value) => {
              const findRole = roleOptions?.find(
                (option) => option?.value === value,
              );
              form.setFieldValue(
                ["ticket_roles", field.name, "dept_name"],
                findRole?.label,
              );
              form.setFieldValue(
                ["ticket_roles", field.name, "employee_id"],
                undefined,
              );
            }}
          />
        </Form.Item>
        <Form.Item
          name={[field.name, "employee_id"]}
          rules={[{ required: true, message: "Please select a employee" }]}
          className="w-48 mb-0"
        >
          <Select
            size="large"
            allowClear
            placeholder="Employee"
            suffixIcon={<CaretDownIcon />}
            options={employeeOptions || []}
            disabled={isEmployeesLoading || !deptId}
          />
        </Form.Item>

        <Form.Item
          name={[field.name, "role_type"]}
          initialValue={
            templateType === TEMPLATE_TYPES.sla ? "checker" : undefined
          }
          rules={[{ required: true, message: "Please select a task" }]}
          className="w-36 mb-0"
        >
          <Select
            size="large"
            allowClear
            placeholder="Task"
            suffixIcon={<CaretDownIcon />}
            options={
              templateType === TEMPLATE_TYPES.sla
                ? [{ label: "L1", value: "checker" }]
                : [
                    { label: "Checker", value: "checker" },
                    { label: "Approver", value: "approver" },
                    { label: "Executor", value: "executor" },
                  ]
            }
          />
        </Form.Item>

        <div className="flex items-center gap-2">
          <TimeIcon />
          <div className="flex items-center gap-1">
            <Form.Item
              name={[field.name, "visibility_duration_hours"]}
              className="mb-0"
              rules={[
                {
                  required: true,
                  message: "Hour required",
                },
                {
                  validator: (_, value) => {
                    // If empty, skip this validator — 'required' rule will handle it
                    if (value === undefined || value === null || value === "") {
                      return Promise.resolve();
                    }

                    // If not a number or not an integer
                    if (!/^\d+$/.test(value)) {
                      return Promise.reject("Hour must be an integer");
                    }

                    // Negative check
                    if (Number(value) < 0) {
                      return Promise.reject("Hour cannot be negative");
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber
                size="large"
                placeholder="HH"
                className="border-none w-16 text-center"
                min={0}
                step={1}
                stringMode={false}
                precision={0}
                max={60}
              />
            </Form.Item>
            <span className="font-semibold">:</span>
            <Form.Item shouldUpdate noStyle>
              {({ getFieldValue }) => {
                return (
                  <Form.Item
                    name={[field.name, "visibility_duration_minutes"]}
                    dependencies={[
                      ["ticket_roles", field.name, "visibility_duration_hours"],
                    ]}
                    className="mb-0"
                    rules={[
                      ({ getFieldValue }) => ({
                        validator: (_, value) => {
                          const hourValue = getFieldValue([
                            "ticket_roles",
                            field.name,
                            "visibility_duration_hours",
                          ]);

                          if (
                            value === undefined ||
                            value === null ||
                            value === ""
                          ) {
                            return Promise.resolve();
                          }

                          if (!/^\d+$/.test(value)) {
                            return Promise.reject("Minute must be an integer");
                          }

                          const minute = Number(value);

                          if (minute < 0) {
                            return Promise.reject("Minute cannot be negative");
                          }

                          if (minute > 59) {
                            return Promise.reject("Minute must be ≤ 59");
                          }

                          if ((hourValue === 0 || !hourValue) && minute < 1) {
                            return Promise.reject("Minute must be > 0");
                          }

                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <InputNumber
                      size="large"
                      placeholder="MM"
                      className="border-none w-16 text-center"
                      min={0}
                      max={59}
                      step={1}
                      stringMode={false}
                      precision={0}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </div>
        </div>

        {!!(templateType !== TEMPLATE_TYPES.sla) && (
          <div className="flex items-center gap-2 mt-[5px]">
            <span>Bypass</span>
            <Form.Item
              name={[field.name, "bypass"]}
              valuePropName="checked"
              initialValue={false}
              className="mb-0"
            >
              <Switch />
            </Form.Item>
          </div>
        )}

        <button
          disabled={totalFields <= 1}
          className="cursor-pointer mt-[18px] disabled:!cursor-not-allowed disabled:!opacity-60"
          onClick={() => totalFields > 1 && remove(field.name)}
        >
          <DeleteIcon />
        </button>
      </div>
    </div>
  );
};

export default TemplateRole;
